import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import dayjs from 'dayjs';
import { completeTask, fetchAllTasks, archiveTask, addTaskComment, updateTask, bulkDeleteTasks, bulkArchiveTasks } from '../../store/actions/TasksActions';
import Badge from '../shared/TailwindComponents/Badge';
import Button from '../shared/TailwindComponents/Button';
import { Task } from './types';
import { formatStatus, formatDateTime } from '../../utils/functions';
import TaskForm from './TaskForm';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import { useHistory, useLocation } from 'react-router-dom';
import { PiFlagFill, PiCheckSquareOffsetLight, PiArchiveLight, PiPaperclipLight, PiChatCircleTextLight, PiCheckCircleLight, PiCheckLight, PiClockClockwiseLight, PiPlusLight, PiTrashLight, PiArrowLeftLight, PiClockCounterClockwiseLight } from 'react-icons/pi';
import Table from '../shared/TailwindComponents/Table';
import { fetchAllEmployees } from '../../store/actions/organization/employeeActions';
import Header from '../shared/TailwindComponents/Header';
import { CheckIcon } from '@heroicons/react/24/solid';
import TaskGridItem from './TaskGridItem';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import ResourceTrash from '../RecycleBin/ResourceTrash';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, taskListTourSteps } from '../shared/FeatureTour';

import ActivityDrawer from '../shared/activity/ActivityDrawer';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import EmptyState from '../shared/TailwindComponents/EmptyState';

interface TaskDeadlineTrackerProps {
  teamId?: string;
}

const TaskDeadlineTracker: React.FC<TaskDeadlineTrackerProps> = ({ teamId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
  const { loading: reduxLoading, error } = useSelector((state: RootState) => state.tasks);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isTasksLoading, setIsTasksLoading] = useState(false);
  const [isAddingTask, setIsAddingTask] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [selectedFilters, setSelectedFilters] = useState<{ [key: string]: string[] }>({ status: ['active'] });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalTasks, setTotalTasks] = useState(0);
  const history = useHistory();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState<any[]>([]);
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [auditTarget, setAuditTarget] = useState<{ type: string; id: string; name: string } | null>(null);
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const { checkSectionGate } = useLicenseGate();

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'task-list',
    steps: taskListTourSteps,
    readyToShow: !isTasksLoading && !isAddingTask && !showTrash,
    autoStartDelay: 600,
  });

  const matterFilterOptions = filterOptions.map(matter => ({
    value: matter.uuid,
    label: matter.name,
  }));

  const taskFilters = [
    {
      id: 'matter', name: t('Matter'), options: matterFilterOptions
    },
    {
      id: 'status', name: 'Status', options: [
        { value: 'active', label: t('Active') },
        { value: 'todo', label: t('Todo') },
        { value: 'in_progress', label: t('In Progress') },
        { value: 'completed', label: t('Completed') },
        { value: 'archived', label: t('Archived') },
      ]
    },
    {
      id: 'priority', name: t('Priority'), options: [
        { value: 'low', label: t('Low') },
        { value: 'medium', label: t('Medium') },
        { value: 'high', label: t('High') },
      ]
    },
  ];

  const sortOptions = [
    { value: 'task_name_asc', label: t('Task Name (A-Z)'), current: sortBy === 'task_name' && sortOrder === 'asc' },
    { value: 'task_name_desc', label: t('Task Name (Z-A)'), current: sortBy === 'task_name' && sortOrder === 'desc' },
    { value: 'due_date_asc', label: t('Due Date (Earliest)'), current: sortBy === 'due_date' && sortOrder === 'asc' },
    { value: 'due_date_desc', label: t('Due Date (Latest)'), current: sortBy === 'due_date' && sortOrder === 'desc' },
    { value: 'priority_asc', label: t('Priority (Low to High)'), current: sortBy === 'priority' && sortOrder === 'asc' },
    { value: 'priority_desc', label: t('Priority (High to Low)'), current: sortBy === 'priority' && sortOrder === 'desc' },
  ];

  const handleBulkDelete = (taskIds: string[]) => {
    if (taskIds.length === 0) {
      dispatch(showFeedbackModal({
        modalType: 'warning',
        message: t('Please select at least one task.'),
        showModal: true,
        duration: 3000,
      }));
      return;
    }
    dispatch(showModal({
      type: 'confirmation',
      message: taskIds.length > 1 ? t(`Delete ${taskIds.length} Tasks`) : t('Delete Task'),
      subMsg: t('This will permanently delete the selected task(s). This action cannot be undone.'),
      icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
      isDest: true,
      onConfirm: async () => {
        await dispatch(bulkDeleteTasks(taskIds) as any);
        setSelectedTasks([]);
        await fetchInitialTasks();
        dispatch(hideModal());
      },
      onCancel: () => dispatch(hideModal()),
      showModal: true,
    }));
  };

  const handleBulkArchive = (taskIds: string[]) => {
    if (taskIds.length === 0) {
      dispatch(showFeedbackModal({
        modalType: 'warning',
        message: t('Please select at least one task.'),
        showModal: true,
        duration: 3000,
      }));
      return;
    }
    dispatch(showModal({
      type: 'confirmation',
      message: taskIds.length > 1 ? t(`Archive ${taskIds.length} Tasks`) : t('Archive Task'),
      subMsg: t('The selected task(s) will be archived. You can restore them later if needed.'),
      icon: <PiArchiveLight className='w-6 h-6 text-danger-600' />,
      onConfirm: async () => {
        await dispatch(bulkArchiveTasks(taskIds) as any);
        setSelectedTasks([]);
        await fetchInitialTasks();
        dispatch(hideModal());
      },
      onCancel: () => dispatch(hideModal()),
      showModal: true,
    }));
  };

  const bulkActions = [
    {
      label: t('Archive Tasks'),
      onClick: () => handleBulkArchive(selectedTasks),
      icon: <PiArchiveLight className='w-4 h-4' />,
    },
    {
      label: t('Delete Tasks'),
      onClick: () => handleBulkDelete(selectedTasks),
      icon: <PiTrashLight className='w-4 h-4' />,
    },
  ];

  const actions = [
    {
      label: t('Add Task'),
      icon: <PiPlusLight className='w-4 h-4' />,
      onClick: () => {
        if (checkSectionGate('tasks')) {
          setIsAddingTask(true);
          history.push('/tasks?add=true');
        }
      },
      requiresSubscription: false,
    },
  ];

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('add') === 'true') {
      if (checkSectionGate('tasks')) {
        setIsAddingTask(true);
      } else {
        history.replace('/tasks');
      }
    } else {
      setIsAddingTask(false);
    }
  }, [location]);

  const handleCancelAddTask = () => {
    setIsAddingTask(false);
    if (!teamId) {
      history.push('/tasks');
    }
  };

  const handleTaskAdded = () => {
    setIsAddingTask(false);
  };

  const fetchInitialTasks = useCallback(async () => {
    setIsTasksLoading(true);
    try {
      const resp = await dispatch(fetchAllTasks(buildQueryParams(1)) as any);
      const { tasks: newTasks = [], total_count = 0, total_pages = 0, filter_options = [] } = resp;
      setTasks(newTasks);
      setSelectedTasks([]);
      setPage(1);
      const isLastPage = 1 >= total_pages || newTasks.length < 12;
      setHasMore(!isLastPage);
      setTotalTasks(total_count);
      setFilterOptions(filter_options);
    } catch (error: any) {
      console.error('Error fetching tasks:', error);
      setTasks([]);
      setPage(1);
      setHasMore(false);
      setTotalTasks(0);
      setFilterOptions([]);
    } finally {
      setIsTasksLoading(false);
    }
  }, [dispatch, searchQuery, selectedFilters, sortBy, sortOrder]);

  const handleCompleteTask = async (taskUuid: string) => {
    try {
      dispatch(
        showModal({
          type: 'addTaskComment',
          message: t('Complete Task'),
          icon: <PiCheckCircleLight className='w-6 h-6 text-success-600' />,
          subMsg: t('Add an optional comment to let others know about the completion.'),
          onConfirm: async (comment: string) => {
            setCommentsLoading(true);
            try {
              await dispatch(completeTask(taskUuid) as any);
              if (comment.trim()) {
                await dispatch(addTaskComment(taskUuid, comment) as any);
              }
              await fetchInitialTasks();
              dispatch(hideModal());
            } catch (error) {
              dispatch(showFeedbackModal({
                modalType: 'error',
                message: t('Error completing task. Please try again later.'),
                showModal: true,
                duration: 3000,
              }));
            } finally {
              setCommentsLoading(false);
            }
          },
          showModal: true,
          onCancel: () => dispatch(hideModal()),
          duration: 3000,
        })
      );
    } catch (error) {
      console.error(t('Error completing task. Please try again later.'), error);
      setCommentsLoading(false);
    }
  };

  const handleArchiveTask = async (taskUuid: string) => {
    try {
      dispatch(
        showModal({
          showModal: true,
          type: 'confirmation',
          onConfirm: async () => {
            try {
              await dispatch(archiveTask(taskUuid) as any);
              await fetchInitialTasks();
              dispatch(hideModal());
            } catch (error) {
              dispatch(showFeedbackModal({
                modalType: 'error',
                message: t('Error archiving task. Please try again later.'),
                showModal: true,
                duration: 3000,
              }));
              dispatch(hideModal());
            }
          },
          message: t('Archive Task'),
          subMsg: t('This task will be archived. You can restore it later if needed.'),
          icon: <PiArchiveLight className='w-6 h-6 text-danger-600' />,
        })
      );
    } catch (error) {
      console.error(t('Error archiving task. Please try again later.'), error);
    }
  };

  const handleRestoreTask = async (taskUuid: string) => {
    try {
      await dispatch(updateTask({ TaskID: taskUuid, Status: 'todo' }) as any);
      dispatch(showFeedbackModal({
        modalType: 'success',
        message: t('Task restored.'),
        showModal: true,
        duration: 3000
      }));
      await fetchInitialTasks();
    } catch (error) {
      console.error(t('Error restoring task. Please try again later.'), error);
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Error restoring task. Please try again later.'),
        showModal: true,
        duration: 3000,
      }));
    }
  };

  const handleDetails = (taskUuid: string) => {
    dispatch(fetchAllEmployees());
    history.push(`/tasks/${taskUuid}`);
  };

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    setSelectedFilters((prev) => ({ ...prev, [filterName]: selectedValues }));
    setPage(1);
  };

  const handleSortChange = (newSortBy: string, newSortOrder?: 'asc' | 'desc') => {
    // Handle both direct calls from table headers (with two params)
    // and calls from FilterBarWithTabs (with encoded value like "field_order")
    if (newSortOrder) {
      /* Stored VERBATIM - `Table` compares its own header-derived key against
         `sortBy` to decide the arrow direction, so translating here would make
         every Name click read as a fresh ascending sort. The endpoint's
         spelling is applied in `buildQueryParams` instead. */
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
    } else {
      // Decode the sort value from FilterBarWithTabs (format: "field_order")
      const parts = newSortBy.split('_');
      const order = parts.pop() as 'asc' | 'desc';
      const field = parts.join('_');
      setSortBy(field);
      setSortOrder(order);
    }
    setPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setPage(1);
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({ status: ['active'] });
    setSearchQuery('');
    setSortBy('');
    setSortOrder('asc');
    setPage(1);
  };

  const buildQueryParams = (pageNum: number): Record<string, any> => {
    const params: Record<string, any> = {
      page: pageNum,
      limit: 50,
    };

    if (searchQuery) {
      params.search = searchQuery;
    }

    if (selectedFilters.matter?.length > 0) {
      params.matter_uuid = selectedFilters.matter[0];
    }

    if (selectedFilters.status?.length > 0) {
      params.status = selectedFilters.status[0];
    }

    if (selectedFilters.priority?.length > 0) {
      params.priority = selectedFilters.priority[0];
    }

    // Add sort parameters. The Name column header yields `name`; the endpoint
    // and the card view's sort menu both call that field `task_name`.
    if (sortBy) {
      params.sort_by = sortBy === 'name' ? 'task_name' : sortBy;
      params.sort_order = sortOrder;
    }

    return params;
  };

  const loadMoreTasks = useCallback(async () => {
    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;
      const params = buildQueryParams(nextPage);
      const response = await dispatch(fetchAllTasks(params) as any);

      if (response && response.tasks !== undefined) {
        const { tasks: newTasks = [], total_count = 0, total_pages = 0, page: currentPage = nextPage, filter_options = [] } = response;
        setTasks((prev) => [...prev, ...newTasks]);
        setPage(nextPage);
        const isLastPage = nextPage >= total_pages || newTasks.length < 12;
        setHasMore(!isLastPage);
        setTotalTasks(total_count);
        setFilterOptions(filter_options);
      }
    } catch (error: any) {
      console.error('Error loading more tasks:', error);
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: error.message || t('Failed to fetch more tasks. Please try again.'),
        showModal: true,
        duration: 3000,
      }));
    } finally {
      setIsLoadingMore(false);
    }
  }, [dispatch, page, searchQuery, selectedFilters, t]);

  useEffect(() => {
    if (isAddingTask) return;
    setPage(1);
    fetchInitialTasks();
  }, [searchQuery, selectedFilters, sortBy, sortOrder, isAddingTask, fetchInitialTasks]);

  useEffect(() => {
    if (error) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t(error || 'An error occurred while fetching tasks.'),
        showModal: true,
        duration: 3000,
      }));
    }
  }, [error, dispatch, t]);

  const columns = ["Name", "Matter", "Status", "Due Date", "Priority", "Access", "Attachments", "Comments", "Actions"];

  const getStatusBadgeColor = (status?: string): 'gray' | 'blue' | 'yellow' | 'green' | 'red' => {
    if (!status) return 'gray';
    const lowerStatus = status.toLowerCase();
    if (lowerStatus === 'todo' || lowerStatus === 'active') return 'blue';
    if (lowerStatus === 'in_progress') return 'blue';
    if (lowerStatus === 'on_hold') return 'yellow';
    if (lowerStatus === 'completed') return 'green';
    if (lowerStatus === 'archived' || lowerStatus === 'deleted') return 'red';
    return 'gray';
  };

  const getPriorityColor = (priority?: string): string => {
    const lowerPriority = priority?.toLowerCase() || '';
    if (lowerPriority === 'critical') return 'red';
    if (lowerPriority === 'high') return 'orange';
    if (lowerPriority === 'medium') return 'yellow';
    if (lowerPriority === 'low') return 'green';
    return 'gray';
  };

  const tableData = tasks.map((task) => {
    const collaborators = task.collaborators || task.Collaborators || [];
    const taskStatus = task.Status || 'todo';
    const dueDate = task.TaskDeadline || task.DueDate || '';
    const priority = task.Priority || 'medium';

    const attachmentCount = typeof task.attachment_count === 'number' ? task.attachment_count : (task.Attachments ? task.Attachments.length : 0);
    const commentCount = typeof task.comment_count === 'number' ? task.comment_count : (task.Comments ? task.Comments.length : 0);

    const matterName = task.matter_name || task.matter?.title || '';
    const matterUuid = task.matter?.uuid;

    return {
      id: task.uuid || task.TaskID,
      uuid: task.uuid,
      "Name": task.Title || task.TaskTitle || '',
      "Matter": matterUuid ? (
        <button
          onClick={(e) => {
            e.stopPropagation();
            history.push(`/matters/${matterUuid}`);
          }}
          className="text-sm text-fg-strong hover:underline underline-offset-2 transition-all text-start truncate block max-w-full"
          title={matterName}
        >
          {matterName}
        </button>
      ) : (
        <span className="text-fg-muted text-sm">—</span>
      ),
      Status: (
        <Badge color={getStatusBadgeColor(taskStatus)}>
          {taskStatus === 'deleted' ? t('Archived') : formatStatus(taskStatus)}
        </Badge>
      ),
      "Due Date": dueDate ? formatDateTime(dueDate) : 'N/A',
      Priority: (
        <div className="flex items-center gap-1">
          <PiFlagFill className={`w-4 h-4 text-${getPriorityColor(priority)}-500`} />
          <span className="text-sm">{formatStatus(priority || t('None'))}</span>
        </div>
      ),
      /* The column is declared "Access"; this row key used to be
         "Collaborators", so the cell never matched its column and the people
         column rendered empty on every row. */
      Access: collaborators && collaborators.length > 0 ? (
        <CollaboratorStack collaborators={collaborators} max={3} size="md" />
      ) : (
        <span className="text-fg-muted text-sm">—</span>
      ),
      Attachments: (
        <div className="flex items-center gap-1 text-sm">
          <PiPaperclipLight className="w-4 h-4" />
          <span>{attachmentCount}</span>
        </div>
      ),
      Comments: (
        <div className="flex items-center gap-1 text-sm">
          <PiChatCircleTextLight className="w-4 h-4" />
          <span>{commentCount}</span>
        </div>
      ),
      Actions: null,
      rawTask: task,
      task_name: task.Title || task.TaskTitle || '',
      due_date: dueDate,
      priority: priority,
    };
  });

  const canUserManageTask = (task: Task): boolean => {
    if (!task || !userInfo) return false;

    const isAssignee = task.UserID === userInfo.UserID;
    const isCreator = task.AssignedBy === userInfo.UserID;

    if (isAssignee || isCreator) return true;

    const collaborators = task.collaborators || task.Collaborators || [];
    const userAsCollaborator = collaborators.find((collab: any) => {
      const collabUserId = collab.user_id || collab.UserID;
      const collabEmail = collab.email || collab.Email;
      return collabUserId === userInfo.UserID || collabEmail === userInfo.Email;
    });

    if (!userAsCollaborator) return false;

    const access = userAsCollaborator.role || 'view';
    return access === 'editor' || access === 'admin';
  };

  const taskActions = [
    {
      label: t('Activity'),
      onClick: (row: any) => setAuditTarget({ type: 'task', id: row.uuid || row.id, name: row['Name'] || '' }),
      icon: <PiClockCounterClockwiseLight className="h-4 w-4" />,
    },
    {
      label: t('Complete'),
      onClick: (row: any) => handleCompleteTask(row.uuid || row.id),
      icon: <CheckIcon className="h-4 w-4" />,
      disabled: (row: any) => {
        const taskStatus = (row.rawTask.Status || 'todo').toLowerCase();
        const isActive = taskStatus === 'todo' || taskStatus === 'active';
        return !canUserManageTask(row.rawTask) || !isActive;
      },
    },
    {
      label: t('Archive'),
      onClick: (row: any) => handleArchiveTask(row.uuid || row.id),
      icon: <PiArchiveLight className="h-4 w-4 text-danger-600" />,
      isDestructive: true,
      disabled: (row: any) => {
        const taskStatus = (row.rawTask.Status || 'todo').toLowerCase();
        const isActive = taskStatus === 'todo' || taskStatus === 'active';
        return !canUserManageTask(row.rawTask) || !isActive;
      },
    },
    {
      label: t('Restore'),
      onClick: (row: any) => handleRestoreTask(row.uuid || row.id),
      icon: <PiClockClockwiseLight className="h-4 w-4" />,
      disabled: (row: any) => {
        const taskStatus = (row.rawTask.Status || 'todo').toLowerCase();
        const isArchivedOrCompleted = taskStatus === 'archived' || taskStatus === 'deleted' || taskStatus === 'completed';
        return !canUserManageTask(row.rawTask) || !isArchivedOrCompleted;
      },
    },
  ];

  const totalActiveFilters = Object.values(selectedFilters).reduce((acc, curr) => acc + curr.length, 0);

  return (
    <div className="flex flex-col h-full">
      {!isAddingTask && (
        <>
          <div data-tour-id="task-list-header">
            <Header actions={actions} subscriptionActive={subscriptionActive} teamId={teamId} />
          </div>
          {!teamId && (
            <div data-tour-id="task-list-filters">
            <FilterBarWithTabs
              itemCount={tasks.length}
              totalItemCount={totalTasks}
              itemCountLabel="tasks"
              filters={taskFilters}
              selectedFilters={selectedFilters}
              onFilterChange={handleFilterChange}
              onSearchChange={handleSearchChange}
              onClearAllFilters={handleClearAllFilters}
              viewMode={viewMode}
              setViewMode={setViewMode}
              sortOptions={viewMode === 'grid' ? sortOptions : []}
              onSortChange={viewMode === 'grid' ? handleSortChange : undefined}
              actions={[
                ...bulkActions,
                ...(showTrash ? trashBulkActions : []),
              ]}
              selectionCount={showTrash ? trashSelectedCount : (selectedTasks.length > 0 ? selectedTasks.length : undefined)}
              recycleBinEnabled
              recycleBinActive={showTrash}
              recycleBinLabel={t('Tasks')}
              onRecycleBinToggle={(active) => { if (!active) fetchInitialTasks(); setShowTrash(active); }}
            />
            </div>
          )}

        </>
      )}
      {isAddingTask ? (
        <div className="h-full flex justify-center items-center bg-bg">
          <div className="w-full h-full max-h-screen flex flex-col">
            <TaskForm
              onCancel={handleCancelAddTask}
              onTaskCreated={async (task) => {
                await fetchInitialTasks();
                handleTaskAdded();
              }}
              teamId={teamId}
            />
          </div>
        </div>
      ) : showTrash ? (
        <ResourceTrash
          entityType="task"
          entityLabel="tasks"
          emptyIcon={<PiCheckSquareOffsetLight />}
          onItemRestored={() => fetchInitialTasks()}
          onBulkActionsChange={setTrashBulkActions}
          onSelectedCountChange={setTrashSelectedCount}
        />
      ) : (
        <div className="flex-1 overflow-hidden relative">
          <div data-tour-id="task-list-table" className="absolute top-0 start-0 end-0 h-[120px] pointer-events-none" />
          {viewMode === 'grid' ? (
            /* Vertical inset as well as gutters: the first row of cards used
               to sit flush against the filter bar and the last against the
               Load More rule. The skeleton branch shares the wrapper, so the
               spacing does not jump when the real cards land. */
            <div className="px-4 py-3 h-full">
              {isTasksLoading && tasks.length === 0 ? (
                <Skeleton variant="grid" rows={3} />
              ) : (
                <div className="flex flex-col h-full">
                  <div ref={gridContainerRef} className="flex-grow overflow-y-auto custom-scrollbar-main">
                    {/* Bottom padding inside the scroller so the last card's
                        shadow is not clipped by the overflow edge. */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
                      {tasks.map((task: Task) => (
                        <TaskGridItem
                      key={task.uuid || task.TaskID}
                      task={task}
                      onClick={() => handleDetails(task.uuid || task.TaskID)}
                      currentUserId={userInfo.UserID}
                    />
                  ))}
                  {isLoadingMore && Array.from({ length: 3 }).map((_, index) => (
                    <div
                      key={`skeleton-${index}`}
                      className="glass rounded-lg p-4 shadow-sm animate-fadePulse"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 bg-surface-sunken rounded-full" />
                          <div className="h-4 bg-surface-sunken rounded w-3/4" />
                        </div>
                        <div className="h-4 bg-surface-sunken rounded w-16" />
                      </div>
                      <div className="mt-4 flex flex-col gap-2">
                        <div className="h-3 bg-surface-sunken rounded w-1/2" />
                        <div className="h-3 bg-surface-sunken rounded w-1/3" />
                      </div>
                    </div>
                  ))}
                  {tasks.length === 0 && !isLoadingMore && (
                    <EmptyState
                      className="col-span-full"
                      icon={<PiCheckSquareOffsetLight />}
                      headline={searchQuery || totalActiveFilters > 0 ? t('No results found.') : t('You have no active tasks.')}
                      subtext={searchQuery || totalActiveFilters > 0
                        ? t('Try adjusting your search or filters to find what you’re looking for.')
                        : t('Begin by adding tasks, assigning them to team members or clients to streamline your workflow.')}
                      button={{
                        label: searchQuery || totalActiveFilters > 0 ? t('Clear Search & Filters') : t('Create New Task'),
                        onClick: searchQuery || totalActiveFilters > 0 ? handleClearAllFilters : handleTaskAdded,
                      }}
                    />
                      )}
                    </div>
                  </div>
                  {hasMore && tasks.length > 0 && (
                    <div className="flex justify-center py-6 border-t border-hairline">
                      <Button
                        variant="outline"
                        size="small"
                        onClick={loadMoreTasks}
                        disabled={isLoadingMore}
                        isLoading={isLoadingMore}
                      >
                        {isLoadingMore ? t('Loading...') : t('Load More')}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* The Table scrolls itself in BOTH axes - its own box is
               `overflow-y-auto overflow-x-auto`. This box adding `overflow-x-auto`
               gave one table two horizontal scrollbars. It measures only. */
            <div className="flex flex-col h-full min-h-0">
                <Table
                  columns={columns}
                  data={isTasksLoading && tasks.length === 0 ? [] : tableData}
                  onRowClick={(row) => handleDetails(row.uuid)}
                  actions={taskActions}
                  enableSelection={true}
                  selectedIds={selectedTasks}
                  onSelect={(id: string) => setSelectedTasks((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id])}
                  onSelectAll={() => setSelectedTasks(selectedTasks.length === tasks.length ? [] : tasks.map((t: any) => t.uuid || t.TaskID))}
                  sortableColumns={['Name', 'Due Date', 'Priority']}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSortChange}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMore || (isTasksLoading && tasks.length === 0)}
                  onLoadMore={loadMoreTasks}
                  total_items={totalTasks}
                  total_showing_items={tasks.length}
                  setIsShowing={true}
                  showingLabel={t('tasks')}
                  selectedCount={selectedTasks.length}
                  /* The box above is `h-full overflow-y-hidden`, so the height
                     is already measured for us. Table's default cap is a
                     viewport guess at the chrome overhead; it only ever falls
                     SHORT of the real space, which is how the count row's
                     height would have been left as a gap. */
                  max="max-h-none"
                  min="min-h-0"
                  resizableColumns={true}
                  /* KEY BUMP. Widths persist under `table:/tasks:<tableId>`,
                     so anyone who has visited /tasks before carries the old
                     ~72px Name column in localStorage. A new id retires that
                     entry and hands them the corrected defaults once. */
                  tableId="tasks-v2"
                  defaultColumnWidths={{
                    /* Was 'Task Name', which matched no column - so Name fell
                       through to Table's header-text estimate (~72px). */
                    'Name': 440,
                    'Matter': 200,
                    'Status': 140,
                    'Due Date': 200,
                    'Priority': 140,
                    'Access': 180,
                    'Attachments': 140,
                    'Comments': 140,
                    'Actions': 48,
                  }}
                  emptyState={{
                    icon: <PiCheckSquareOffsetLight />,
                    headline: searchQuery || totalActiveFilters > 0 ? t('No results found.') : t('You have no active tasks at this time.'),
                    subtext: searchQuery || totalActiveFilters > 0
                      ? t('Try adjusting your search or filters to find what you’re looking for.')
                      : t('Begin by adding tasks, assigning them to team members or clients to streamline your workflow.'),
                    button: {
                      label: searchQuery || totalActiveFilters > 0 ? t('Clear Search & Filters') : t('Create New Task'),
                      onClick: searchQuery || totalActiveFilters > 0 ? handleClearAllFilters : () => history.push('/tasks?add=true'),
                    }
                  }}
                />
            </div>
          )}
        </div>
      )}
      <FeatureTour
        steps={taskListTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
      {auditTarget && (
        <ActivityDrawer onClose={() => setAuditTarget(null)}>
          <AuditLogPanel
            entityType={auditTarget.type}
            entityId={auditTarget.id}
            entityName={auditTarget.name}
            onClose={() => setAuditTarget(null)}
          />
        </ActivityDrawer>
      )}
    </div>
  );
};

export default TaskDeadlineTracker;
